import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { link } from '../lib/deepLink';
import { dash, money } from './DataTable';

/**
 * 🔴 청구 대기 영업정산(2026-10-02 way) — 기사 가감이 있는데 그 접수가 그 달 **청구·입금 전**이라(미수 게이트 · 레거시 규칙 그대로)
 * 명세·정산현황에 **안 들어가는** 줄. OPS 선지급·대체교통비는 돈이 이미 나갔으므로 조용히 빠지면 안 된다.
 * 정산현황 위 · 명세 생성 미리보기가 같은 목록(`listResultReqHeld` → 서버 `SQL_REQ_HELD`)을 띄운다.
 */
export interface HeldReq {
  result_req_id: number; recv_id: number; dispt_id: number | null; drive_mon: string; recv_type: string | null; cust_name: string | null;
  driver_id: number | null; driver_name: string | null; owner_id: number | null; owner_name: string | null; car_no: string | null;
  account: string | null; driver_plus: string; driver_minus: string;
  ops_dy_no: string | null; ops_cash_book_id: string | null; ops_cash_date: string | null; misu_state: string | null;
}

const chip = 'rounded border border-line bg-surface px-1.5 py-0.5 text-[11px] text-brand-700 hover:bg-brand-50';

export default function HeldReqBanner({ driveMon, context }: { driveMon: string; context: 'status' | 'gen' }) {
  const [rows, setRows] = useState<HeldReq[] | null>(null);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(driveMon)) { setRows(null); return; }
    let alive = true;
    setRows(null); setErr(null);
    api.listResultReqHeld({ driveMon })
      .then((r) => { if (alive) setRows(r.rows as unknown as HeldReq[]); })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [driveMon]);

  if (err) return <p className="text-xs text-red-700">청구 대기 영업정산을 못 읽었습니다: {err}</p>;
  if (!rows || rows.length === 0) return null;
  const ops = rows.filter((r) => r.ops_dy_no);
  const minus = rows.reduce((s, r) => s + Number(r.driver_minus), 0);
  const plus = rows.reduce((s, r) => s + Number(r.driver_plus), 0);
  const shown = open ? rows : ops;

  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${ops.length ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <b className={ops.length ? 'text-red-800' : 'text-amber-900'}>
          청구 대기 영업정산 {rows.length}줄{ops.length ? ` · 그중 OPS 선지급·대중교통비 ${ops.length}줄` : ''}
        </b>
        <span className="text-xs text-ink-soft">기사 공제 {money(minus)} · 지급 {money(plus)}</span>
        {ops.length < rows.length && (
          <button className="btn-ghost ml-auto py-0.5 text-xs" onClick={() => setOpen((v) => !v)}>
            {open ? (ops.length ? 'OPS 줄만' : '접기') : `${ops.length ? '전체 ' : ''}${rows.length}줄 보기`}
          </button>
        )}
      </div>
      <p className="mt-0.5 text-xs text-ink-soft">
        접수가 이 달 <b>청구 요청(청구대상·청구완료) 또는 입금(완료·예정)</b>되기 전이라 {context === 'gen' ? '지금 명세를 만들면 이 줄은 빠집니다' : '정산현황·명세에 안 들어갑니다'}.
        미수금에서 그 접수를 청구하면 들어갑니다.{ops.length ? ' 🔴 OPS 줄은 돈이 이미 나갔습니다 — 꼭 확인하세요.' : ''}
      </p>
      {shown.length > 0 && (
        <ul className="mt-1 max-h-64 divide-y divide-line overflow-y-auto rounded border border-line bg-surface">
          {shown.map((r) => (
            <li key={r.result_req_id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-2 py-1 text-xs">
              {r.ops_dy_no && <span className="rounded bg-red-100 px-1 font-mono text-[11px] text-red-800">{r.ops_dy_no}</span>}
              <span className="font-medium">{dash(r.driver_name ?? r.owner_name)}</span>
              {r.owner_name && r.driver_name && r.owner_name !== r.driver_name && <span className="text-ink-faint">차주 {r.owner_name}</span>}
              <span className="text-ink-soft">접수 {r.recv_id} {dash(r.cust_name)}</span>
              <span>{dash(r.account)}</span>
              <span className="font-medium tabular-nums">
                {Number(r.driver_minus) ? `−${money(r.driver_minus)}` : money(r.driver_plus)}
              </span>
              <span className="text-ink-faint" title="그 접수·정산월의 미수 상태(청구/입금)">미수 {r.misu_state ?? '등록 없음'}</span>
              <span className="ml-auto flex flex-wrap gap-1">
                <Link className={chip} to={link.resultReq(r.result_req_id, r.drive_mon)}>영업정산</Link>
                {r.ops_cash_book_id && <Link className={chip} to={link.cashBook(r.ops_cash_book_id, r.ops_cash_date)}>출납일보</Link>}
                {r.ops_dy_no && <Link className={chip} to={link.opsTransit(r.ops_dy_no)}>OPS 탭</Link>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
